/**
 * Share URL utilities for Prompt Playground
 */

export interface ShareData {
  query: string;
  prompt: string;
  results: string;
  resultImages: string[];
}

const SHARE_PARAM_NAME = "share";

/**
 * Gets the share ID from the current URL query parameters
 * @returns The share ID if present, null otherwise
 */
export function getShareIdFromUrl(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(SHARE_PARAM_NAME);
}

/**
 * Removes the share parameter from the current URL without triggering a page reload
 */
export function cleanShareParamsFromUrl(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete(SHARE_PARAM_NAME);
  window.history.replaceState({}, document.title, url.toString());
}

/**
 * Generates a short share URL with the given share ID
 * @param shareId - The unique share identifier
 * @returns The full share URL
 */
export function generateShortShareUrl(shareId: string): string {
  const url = new URL(window.location.href);
  // Clear existing search params and set only the share param
  url.search = "";
  url.searchParams.set(SHARE_PARAM_NAME, shareId);
  return url.toString();
}

/**
 * Copies the given text to the clipboard
 * @param text - The text to copy
 * @returns Promise that resolves to true if successful, false otherwise
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    // Fallback for older browsers
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-9999px";
    textArea.style.top = "-9999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const success = document.execCommand("copy");
    document.body.removeChild(textArea);
    return success;
  } catch {
    return false;
  }
}
